// src/components/diversion/RaiseDiversionRequestModal.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import diversionService from '../../services/diversionService';
import poiMappingService from '../../services/poiMappingService';
import poAssignedService from '../../services/poAssignedService';
import httpClient from '../../services/httpClient';
import '../../styles/materialDiversion.css';

// Helper to format PO and Item Sr cleanly, e.g. 26255133103381/003
const formatPoAndItem = (poNo, poSerialNo) => {
  if (!poNo && !poSerialNo) return '-';
  const cleanPo = String(poNo || '').trim();
  const rawSr = String(poSerialNo || '').trim();

  if (rawSr.includes('/')) {
    const parts = rawSr.split('/');
    const lastPart = parts[parts.length - 1];
    return cleanPo ? `${cleanPo}/${lastPart}` : rawSr;
  }

  if (cleanPo && rawSr) {
    return `${cleanPo}/${rawSr}`;
  }
  return cleanPo || rawSr;
};

// Helper to normalize and compare serial numbers safely
const normalizeSerial = (sr) => {
  if (sr === null || sr === undefined) return '';
  const s = String(sr).trim();
  if (s.includes('/')) {
    const parts = s.split('/');
    const lastPart = parts[parts.length - 1];
    const parsed = parseInt(lastPart, 10);
    return isNaN(parsed) ? lastPart.toLowerCase() : String(parsed);
  }
  const parsed = parseInt(s, 10);
  return isNaN(parsed) ? s.toLowerCase() : String(parsed);
};

// Modern Searchable & Scrollable Dropdown for IC Selection
const SearchableIcDropdown = ({ options = [], value = '', onChange, placeholder = '-- Select Completed Source IC --', loading = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.certificateNo === value);
  }, [options, value]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter(opt =>
      (opt.certificateNo && opt.certificateNo.toLowerCase().includes(term)) ||
      (opt.poNo && String(opt.poNo).toLowerCase().includes(term)) ||
      (opt.poSerialNo && String(opt.poSerialNo).toLowerCase().includes(term)) ||
      (opt.availableBalanceQty !== undefined && String(opt.availableBalanceQty).includes(term))
    );
  }, [options, searchTerm]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (certNo) => {
    onChange(certNo);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-select-container" ref={containerRef}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        tabIndex={0}
        role="button"
      >
        <div className="searchable-select-trigger-content">
          {selectedOption ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span className="searchable-select-ic-badge">{selectedOption.certificateNo}</span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  ({formatPoAndItem(selectedOption.poNo, selectedOption.poSerialNo)})
                </span>
              </div>
              <span className="searchable-select-bal-badge">
                Bal: {selectedOption.availableBalanceQty} {selectedOption.unitOfMeasurement || 'MT'}
              </span>
            </div>
          ) : value ? (
            <span className="searchable-select-ic-badge">{value}</span>
          ) : (
            <span style={{ color: '#94a3b8' }}>{placeholder}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {(selectedOption || value) && (
            <button
              type="button"
              className="searchable-select-clear-btn"
              title="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
            >
              ✕
            </button>
          )}
          <span className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
      </div>

      {isOpen && (
        <div className="searchable-select-menu">
          <div className="searchable-select-search-box">
            <span className="searchable-select-search-icon">🔍</span>
            <input
              ref={inputRef}
              type="text"
              className="searchable-select-search-input"
              placeholder="Search by IC No, PO No, Item Sr..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                type="button"
                className="searchable-select-clear-btn"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="searchable-select-options">
            {loading ? (
              <div className="searchable-select-empty">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                  <span style={{
                    display: 'inline-block', width: 16, height: 16,
                    border: '2px solid #e2e8f0', borderTopColor: '#2563eb',
                    borderRadius: '50%', animation: 'spin 0.7s linear infinite'
                  }} />
                  Loading ICs...
                </span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">
                <span>No matching source ICs found</span>
                <small style={{ color: '#cbd5e1' }}>Try searching with a different term</small>
              </div>
            ) : (
              filteredOptions.map((ic) => {
                const isSelected = ic.certificateNo === value;
                return (
                  <div
                    key={ic.certificateNo}
                    className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(ic.certificateNo)}
                  >
                    <div className="searchable-select-option-header">
                      <span className="searchable-select-ic-badge">{ic.certificateNo}</span>
                      <span className="searchable-select-bal-badge">
                        Bal: {ic.availableBalanceQty} {ic.unitOfMeasurement || 'MT'}
                      </span>
                    </div>
                    <div className="searchable-select-option-sub">
                      <span>📄 PO / Item: <strong>{formatPoAndItem(ic.poNo, ic.poSerialNo)}</strong></span>
                      {ic.callNo && <span>📞 Call: {ic.callNo}</span>}
                    </div>
                    <div className="searchable-select-option-sub" style={{ marginTop: '2px', gap: '12px' }}>
                      {ic.ieName && (
                        <span style={{ color: '#0f172a', fontSize: '11px' }}>
                          👤 IE: <strong>{ic.ieName}</strong>
                        </span>
                      )}
                      {ic.certDocUrl && (
                        <a
                          href={ic.certDocUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '11px', color: '#0284c7', textDecoration: 'none', fontWeight: 600 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          📋 View Certificate
                        </a>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Generic Modern Select Dropdown
const ModernSelect = ({ options = [], value = '', onChange, placeholder = '-- Select an option --' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value) || null;
  }, [options, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="searchable-select-container" ref={containerRef}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
        role="button"
      >
        <div className="searchable-select-trigger-content">
          {selectedOption ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedOption.icon && <span className="modern-select-icon">{selectedOption.icon}</span>}
              <span style={{ fontWeight: 500, color: '#0f172a' }}>{selectedOption.label}</span>
            </div>
          ) : (
            <span style={{ color: '#94a3b8' }}>{placeholder}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {selectedOption && (
            <button
              type="button"
              className="searchable-select-clear-btn"
              title="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
            >
              ✕
            </button>
          )}
          <span className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
      </div>

      {isOpen && (
        <div className="searchable-select-menu">
          <div className="searchable-select-options">
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <div
                  key={opt.value}
                  className={`modern-select-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                >
                  {opt.icon && <span className="modern-select-icon">{opt.icon}</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: isSelected ? 600 : 400 }}>{opt.label}</div>
                    {opt.subtext && <div style={{ fontSize: '11px', color: '#64748b' }}>{opt.subtext}</div>}
                  </div>
                  {isSelected && <span style={{ color: '#0284c7', fontWeight: 'bold' }}>✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Searchable Manufacturing Plant Dropdown with Address Display
const SearchablePlantDropdown = ({ options = [], value = '', address = '', poiCode = '', onChange, placeholder = '-- Select Manufacturing Plant --' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const getPlantName = (opt) => (typeof opt === 'string' ? opt : (opt.unitName || opt.unit_name || opt.name || ''));
  const getPlantAddress = (opt) => (typeof opt === 'string' ? '' : (opt.address || ''));
  const getPoiCode = (opt) => (typeof opt === 'string' ? '' : (opt.poiCode || opt.poi_code || ''));

  const selectedOption = useMemo(() => {
    return options.find(opt => getPlantName(opt) === value);
  }, [options, value]);

  const displayAddress = address || (selectedOption ? getPlantAddress(selectedOption) : '');
  const displayPoiCode = poiCode || (selectedOption ? getPoiCode(selectedOption) : '');

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter(opt => {
      const pName = getPlantName(opt).toLowerCase();
      const pAddr = getPlantAddress(opt).toLowerCase();
      const pPoi = getPoiCode(opt).toLowerCase();
      return pName.includes(term) || pAddr.includes(term) || pPoi.includes(term);
    });
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="searchable-select-container" ref={containerRef}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        tabIndex={0}
        role="button"
        style={{ minHeight: '44px' }}
      >
        <div className="searchable-select-trigger-content">
          {value ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>🏭</span>
                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}>{value}</span>
                {displayPoiCode && (
                  <span style={{ fontSize: '10.5px', background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                    {displayPoiCode}
                  </span>
                )}
              </div>
              {displayAddress && (
                <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📍 {displayAddress}
                </div>
              )}
            </div>
          ) : (
            <span style={{ color: '#94a3b8' }}>{placeholder}</span>
          )}
        </div>
        <span className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}>▼</span>
      </div>

      {isOpen && (
        <div className="searchable-select-menu">
          {options.length > 3 && (
            <div className="searchable-select-search-box">
              <span className="searchable-select-search-icon">🔍</span>
              <input
                ref={inputRef}
                type="text"
                className="searchable-select-search-input"
                placeholder="Search plant name, address, POI..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">
                <span>No plants found</span>
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const pName = getPlantName(opt);
                const pAddr = getPlantAddress(opt);
                const pPoi = getPoiCode(opt);
                const isSelected = pName === value;

                return (
                  <div
                    key={pName || idx}
                    className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(pName, opt);
                      setIsOpen(false);
                    }}
                  >
                    <div className="searchable-select-option-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>🏭</span>
                        <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}>{pName}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {pPoi && (
                          <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#475569', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            {pPoi}
                          </span>
                        )}
                        {isSelected && <span style={{ color: '#0284c7', fontWeight: 'bold' }}>✓</span>}
                      </div>
                    </div>
                    {pAddr && (
                      <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px', lineHeight: 1.3 }}>
                        📍 {pAddr}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Modern Searchable & Scrollable Dropdown for Target PO Selection
const SearchablePoDropdown = ({ options = [], value = '', onChange, placeholder = '-- Search & Select Target PO --' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(() => {
    return options.find(opt => (opt.poNo === value || opt.po_no === value));
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter(opt => {
      const pNo = String(opt.poNo || opt.po_no || '').toLowerCase();
      const pDes = String(opt.poDes || opt.description || '').toLowerCase();
      const pZone = String(opt.rlyShortName || opt.zone_name || '').toLowerCase();
      return pNo.includes(term) || pDes.includes(term) || pZone.includes(term);
    });
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (poObj) => {
    const pNo = poObj.poNo || poObj.po_no || '';
    onChange(pNo, poObj);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-select-container" ref={containerRef}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        tabIndex={0}
        role="button"
      >
        <div className="searchable-select-trigger-content">
          {selectedOption ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span className="searchable-select-ic-badge" style={{ color: '#0f172a' }}>
                  {selectedOption.poNo || selectedOption.po_no}
                </span>
                {(selectedOption.rlyShortName || selectedOption.zone_name) && (
                  <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                    {selectedOption.rlyShortName || selectedOption.zone_name}
                  </span>
                )}
              </div>
              {(selectedOption.qty || selectedOption.quantity) && (
                <span className="searchable-select-bal-badge" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                  Qty: {selectedOption.qty || selectedOption.quantity} {selectedOption.unit || 'NOS'}
                </span>
              )}
            </div>
          ) : value ? (
            <span className="searchable-select-ic-badge" style={{ color: '#0f172a' }}>{value}</span>
          ) : (
            <span style={{ color: '#94a3b8' }}>{placeholder}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {selectedOption && (
            <button
              type="button"
              className="searchable-select-clear-btn"
              title="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange('', null);
              }}
            >
              ✕
            </button>
          )}
          <span className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
      </div>

      {isOpen && (
        <div className="searchable-select-menu">
          <div className="searchable-select-search-box">
            <span className="searchable-select-search-icon">🔍</span>
            <input
              ref={inputRef}
              type="text"
              className="searchable-select-search-input"
              placeholder="Search by PO number, zone, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                type="button"
                className="searchable-select-clear-btn"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">
                <span>No matching POs found</span>
                <small style={{ color: '#cbd5e1' }}>Try searching with a different term</small>
              </div>
            ) : (
              filteredOptions.map((po, idx) => {
                const pNo = po.poNo || po.po_no || '';
                const isSelected = pNo === value;
                const pZone = po.rlyShortName || po.zone_name;
                const pQty = po.qty || po.quantity;
                const pUnit = po.unit || 'NOS';
                const pDes = po.poDes || po.description;

                return (
                  <div
                    key={pNo || idx}
                    className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(po)}
                  >
                    <div className="searchable-select-option-header">
                      <span className="searchable-select-ic-badge" style={{ color: '#0f172a' }}>{pNo}</span>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {pZone && (
                          <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            {pZone}
                          </span>
                        )}
                        {pQty && (
                          <span className="searchable-select-bal-badge" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                            {pQty} {pUnit}
                          </span>
                        )}
                      </div>
                    </div>
                    {pDes && (
                      <div style={{ fontSize: '11.5px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pDes}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Modern Dropdown for Target PO Item Sr Selection
const SearchablePoItemDropdown = ({ options = [], value = '', onChange, placeholder = '-- Select Item Sr --', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const getSrDisplay = (opt) => {
    if (!opt) return '';
    if (typeof opt === 'string') return opt;
    return opt.poSerialNo || opt.po_serial_no || opt.value || opt.label || '';
  };

  const selectedOption = useMemo(() => {
    return options.find(opt => String(getSrDisplay(opt)) === String(value));
  }, [options, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="searchable-select-container" ref={containerRef}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tabIndex={disabled ? -1 : 0}
        role="button"
      >
        <div className="searchable-select-trigger-content">
          {selectedOption ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>🔢</span>
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{getSrDisplay(selectedOption)}</span>
              {(selectedOption.poDes || selectedOption.item_name) && (
                <span style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  ({selectedOption.poDes || selectedOption.item_name})
                </span>
              )}
            </div>
          ) : value ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>🔢</span>
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{value}</span>
            </div>
          ) : (
            <span style={{ color: '#94a3b8' }}>{disabled ? 'Select Target PO first' : placeholder}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {selectedOption && !disabled && (
            <button
              type="button"
              className="searchable-select-clear-btn"
              title="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
            >
              ✕
            </button>
          )}
          <span className={`searchable-select-chevron ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="searchable-select-menu">
          <div className="searchable-select-options">
            {options.length === 0 ? (
              <div className="searchable-select-empty">
                <span>No available item serial numbers</span>
                <small style={{ color: '#94a3b8' }}>Same source PO & Item Sr is excluded from target</small>
              </div>
            ) : (
              options.map((opt, idx) => {
                const srVal = getSrDisplay(opt);
                const isSelected = String(srVal) === String(value);
                const desc = opt.poDes || opt.item_name || opt.description;
                const qty = opt.orderedQty || opt.item_qty || opt.quantity;

                return (
                  <div
                    key={srVal || idx}
                    className={`modern-select-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      onChange(srVal);
                      setIsOpen(false);
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>🔢</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>Item Sr: {srVal}</div>
                      {desc && <div style={{ fontSize: '11px', color: '#64748b' }}>{desc}</div>}
                    </div>
                    {qty && (
                      <span className="searchable-select-bal-badge" style={{ background: '#f8fafc', color: '#475569' }}>
                        Qty: {qty}
                      </span>
                    )}
                    {isSelected && <span style={{ color: '#0284c7', fontWeight: 'bold' }}>✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const RaiseDiversionRequestModal = ({ isOpen, onClose, onSuccess, vendorUser, isEditMode = false, initialData = null }) => {
  const [stage, setStage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [diversionType, setDiversionType] = useState('COMPLETE');
  const [plantId, setPlantId] = useState('');       // numeric DB id — for POST submit
  const [plantName, setPlantName] = useState('');   // unit name string — for GET queries
  const [plantAddress, setPlantAddress] = useState('');
  const [rioId, setRioId] = useState('');
  const [cmUserId, setCmUserId] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [poiCode, setPoiCode] = useState('');

  // Dropdown lists
  const [plants, setPlants] = useState([]);
  const [cmList, setCmList] = useState([]);
  const [eligibleIcs, setEligibleIcs] = useState([]);
  const [loadingIcs, setLoadingIcs] = useState(false);
  const [selectedIcNo, setSelectedIcNo] = useState('');
  const [selectedIcDetails, setSelectedIcDetails] = useState(null);
  const [lineItems, setLineItems] = useState([]);

  // Target PO
  const [vendorPos, setVendorPos] = useState([]);
  const [targetPoItems, setTargetPoItems] = useState([]);
  const [targetPoNo, setTargetPoNo] = useState('');
  const [targetPoSrNo, setTargetPoSrNo] = useState('');

  // Reason & Permission
  const [reasonCode, setReasonCode] = useState('');
  const [railwayPermissionNo, setRailwayPermissionNo] = useState('');
  const [railwayPermissionDate, setRailwayPermissionDate] = useState(new Date().toISOString().split('T')[0]);
  const [railwayPermissionDocUrl, setRailwayPermissionDocUrl] = useState('');
  const [railwayDocUploading, setRailwayDocUploading] = useState(false);
  const [railwayDocFileName, setRailwayDocFileName] = useState('');
  const [vendorRemarks, setVendorRemarks] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // Track when we are seeding from initialData so CM-reset effect is suppressed
  const seedingRef = useRef(false);

  // 1. Fetch Unit Details (Address, POI Code) on plant selection
  const handlePlantSelect = useCallback(async (selectedUnit, plantObj = null) => {
    setPlantName(selectedUnit);

    let matched = plantObj;
    if (!matched && plants && plants.length > 0) {
      matched = plants.find(p => (typeof p === 'object' && (p.unitName === selectedUnit || p.unit_name === selectedUnit)));
    }

    // plantId must be the numeric DB id (Long), not the unit name string
    setPlantId(matched?.id ?? selectedUnit);

    if (matched && (matched.address || matched.poiCode)) {
      setPlantAddress(matched.address || '');
      setPoiCode(matched.poiCode || '');
      if (matched.rio) setRioId(matched.rio);
      if (matched.companyName) {
        setCompanyName(matched.companyName);
      }
      return;
    }

    if (!selectedUnit) {
      setPlantAddress('');
      setPoiCode('');
      return;
    }

    try {
      const res = await poiMappingService.getUnitDetails(companyName || '', selectedUnit);
      if (res && res.data) {
        setPlantAddress(res.data.address || '');
        setPoiCode(res.data.poiCode || '');
      }
    } catch (err) {
      console.error('Error fetching unit details:', err);
    }
  }, [plants, companyName]);

  const rawVendorCode = vendorUser?.userName || vendorUser?.vendorCode || vendorUser?.id || sessionStorage.getItem('vendorCode') || localStorage.getItem('vendorCode') || '';
  const vendorCode = rawVendorCode.replace(/^:+/, '');

  // Reset all form state when opening for a new request (not edit mode)
  useEffect(() => {
    if (!isOpen || isEditMode) return;
    setStage('');
    setDiversionType('COMPLETE');
    setPlantId('');
    setPlantName('');
    setPlantAddress('');
    setRioId('');
    setCmUserId('');
    setPoiCode('');
    setSelectedIcNo('');
    setSelectedIcDetails(null);
    setLineItems([]);
    setTargetPoNo('');
    setTargetPoSrNo('');
    setTargetPoItems([]);
    setReasonCode('');
    setRailwayPermissionNo('');
    setRailwayPermissionDate(new Date().toISOString().split('T')[0]);
    setRailwayPermissionDocUrl('');
    setRailwayDocFileName('');
    setVendorRemarks('');
    setError('');
  }, [isOpen, isEditMode]);

  // 2. Fetch Plants with Address & Assigned POs on mount when modal is open
  useEffect(() => {
    if (!isOpen || !vendorCode) return;

    // Fetch vendor units with address and POI code
    poiMappingService.getVendorUnits(vendorCode)
      .then(res => {
        const list = res?.data || (Array.isArray(res) ? res : []);
        if (list.length > 0) {
          setPlants(list);
          if (list[0].companyName) {
            setCompanyName(list[0].companyName);
          }
        } else {
          // Fallback to getCompanies if vendor units is empty
          poiMappingService.getCompanies(vendorCode).then(cRes => {
            const cList = cRes?.data || (Array.isArray(cRes) ? cRes : []);
            if (cList.length > 0) {
              const comp = typeof cList[0] === 'string' ? cList[0] : (cList[0].company_name || cList[0].name || '');
              setCompanyName(comp);
              poiMappingService.getUnitsByCompany(comp).then(uRes => {
                const uList = uRes?.data || (Array.isArray(uRes) ? uRes : []);
                setPlants(uList);
              });
            }
          });
        }
      })
      .catch(err => console.error('Error fetching vendor units:', err));

    // Fetch Assigned POs for Target PO Selection
    poAssignedService.getPoAssigned(vendorCode)
      .then(res => {
        const poList = res?.data || (Array.isArray(res) ? res : []);
        setVendorPos(poList);
      })
      .catch(err => console.error('Error fetching vendor POs:', err));

    // Fetch Controlling Managers list
    httpClient.get('/auth/api/controlling-managers')
      .then(res => {
        const list = res?.data || [];
        setCmList(list);
      })
      .catch(err => console.error('Error fetching controlling managers:', err));
  }, [isOpen, vendorCode]);

  // Derive RIO whenever plantName or plants list changes (covers async load order)
  useEffect(() => {
    if (!plantName || !plants.length) return;
    const matched = plants.find(p => p.unitName === plantName || p.unit_name === plantName);
    if (matched?.rio) setRioId(matched.rio);
  }, [plantName, plants]);

  // Seed form from initialData when editing — runs once lists are loaded
  useEffect(() => {
    if (!isEditMode || !initialData || !isOpen) return;
    if (!plants.length) return; // wait for plants to load

    seedingRef.current = true;

    setStage(initialData.stage || '');
    setDiversionType(initialData.diversionType || 'COMPLETE');
    setPlantName(initialData.plantName || '');
    setPlantAddress(initialData.plantAddress || '');
    setRioId(initialData.rioId || '');
    setCmUserId(initialData.cmUserId || '');
    setCompanyName(initialData.companyName || '');
    setReasonCode(initialData.reasonCode || '');
    setRailwayPermissionNo(initialData.railwayPermissionNo || '');
    setRailwayPermissionDate(initialData.railwayPermissionDate || new Date().toISOString().split('T')[0]);
    setVendorRemarks(initialData.vendorRemarks || '');
    setSelectedIcNo(initialData.sourceIcNo || '');
    setTargetPoNo(initialData.targetPoNo || '');
    setTargetPoSrNo(initialData.targetPoSrNo || '');

    // Reconstruct IC details from saved fields so submit payload is correct
    setSelectedIcDetails({
      certificateNo: initialData.sourceIcNo,
      poNo: initialData.sourcePoNo,
      poSerialNo: initialData.sourcePoSrNo,
      callNo: initialData.sourceCallNo,
      icDate: initialData.sourceIcDate,
    });

    // Populate line items from saved items
    if (initialData.items && initialData.items.length > 0) {
      setLineItems(initialData.items.map(item => ({
        ...item,
        diversionQty: item.diversionQty ?? item.totalDiversionQty ?? '',
        availableBalanceQty: item.availableBalanceQty ?? '',
        remainingBalanceQty: Math.max(0, (parseFloat(item.availableBalanceQty) || 0) - (parseFloat(item.diversionQty ?? item.totalDiversionQty) || 0)),
      })));
    }

    // Resolve plantId from plants list
    const matched = plants.find(p => p.unitName === initialData.plantName || p.unit_name === initialData.plantName);
    setPlantId(matched?.id || initialData.plantId || '');
    if (matched?.poiCode) setPoiCode(matched.poiCode);

    // Load railway doc URL separately (excluded from list projection)
    if (initialData.id) {
      diversionService.getDocUrl(initialData.id)
        .then(url => { if (url) setRailwayPermissionDocUrl(url); })
        .catch(() => {});
    }

    // Release seeding lock after all synchronous state updates queued
    setTimeout(() => { seedingRef.current = false; }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, initialData, isOpen, plants]);

  // Seed targetPoItems once vendorPos loads in edit mode
  useEffect(() => {
    if (!isEditMode || !initialData?.targetPoNo || !vendorPos.length) return;
    const poObj = vendorPos.find(p => p.poNo === initialData.targetPoNo || p.po_no === initialData.targetPoNo);
    if (poObj) {
      setTargetPoItems(poObj.poItem || poObj.items || []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, initialData, vendorPos]);

  // Reset CM selection when RIO changes (suppressed during edit-mode seeding)
  useEffect(() => {
    if (seedingRef.current) return;
    setCmUserId('');
  }, [rioId]);

  // CMs filtered to match the selected RIO
  const filteredCmList = useMemo(() => {
    if (!rioId || !cmList.length) return cmList;
    return cmList.filter(cm => cm.rio === rioId);
  }, [cmList, rioId]);

  // Show toast whenever error is set
  useEffect(() => {
    if (!error) return;
    setToast(error);
    const t = setTimeout(() => setToast(''), 7000);
    return () => clearTimeout(t);
  }, [error]);

  // 3. Handle Target PO selection
  const handleTargetPoChange = useCallback((poNumber, poObject) => {
    setTargetPoNo(poNumber);
    if (!poObject && poNumber) {
      poObject = vendorPos.find(p => (p.poNo === poNumber || p.po_no === poNumber));
    }
    const items = poObject?.poItem || poObject?.items || [];
    setTargetPoItems(items);
    setTargetPoSrNo('');
  }, [vendorPos]);

  // Filter out source item serial number if target PO is identical to source PO and sort ascending
  const availableTargetPoItems = useMemo(() => {
    if (!targetPoItems || targetPoItems.length === 0) return [];

    let items = targetPoItems;
    const sourcePo = selectedIcDetails?.poNo || '';
    const sourceSr = selectedIcDetails?.poSerialNo || '';

    if (sourcePo && targetPoNo && String(sourcePo).trim() === String(targetPoNo).trim()) {
      const normSourceSr = normalizeSerial(sourceSr);
      items = targetPoItems.filter(item => {
        const itemSr = item.poSerialNo || item.po_serial_no || item.value || item.label || '';
        return normalizeSerial(itemSr) !== normSourceSr;
      });
    }

    return [...items].sort((a, b) => {
      const srA = String(a.poSerialNo || a.po_serial_no || a.value || a.label || '');
      const srB = String(b.poSerialNo || b.po_serial_no || b.value || b.label || '');
      return srA.localeCompare(srB, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [targetPoItems, targetPoNo, selectedIcDetails]);

  // 4. Fetch eligible ICs only when stage, plant, and vendor are all selected
  useEffect(() => {
    if (!isOpen || !vendorCode || !stage || !plantName) {
      setEligibleIcs([]);
      return;
    }

    // Pass plantName (unit name string) — the SQL filters on ic.unit_name
    setLoadingIcs(true);
    diversionService.getEligibleSourceIcs(vendorCode, plantName, stage)
      .then(res => {
        if (res && res.data) {
          setEligibleIcs(res.data);
        }
      })
      .catch(err => console.error('Error fetching eligible ICs:', err))
      .finally(() => setLoadingIcs(false));
  }, [isOpen, vendorCode, plantName, stage]);

  // Handle IC selection
  const handleIcChange = async (icNo) => {
    setSelectedIcNo(icNo);
    const icObj = eligibleIcs.find(i => i.certificateNo === icNo);
    setSelectedIcDetails(icObj);
    setTargetPoSrNo('');

    // If certDocUrl is missing in the eligible IC object, fetch it directly
    if (icObj && !icObj.certDocUrl && (icNo || icObj.certificateNo || icObj.callNo)) {
      const token = icNo || icObj.certificateNo || icObj.callNo;
      httpClient.get(`/api/certificate-storage/view?icNumber=${encodeURIComponent(token)}`)
        .then(docRes => {
          const docUrl = docRes?.data?.url || (typeof docRes?.data === 'string' ? docRes.data : null);
          if (docUrl) {
            setSelectedIcDetails(prev => prev ? ({ ...prev, certDocUrl: docUrl }) : prev);
          }
        })
        .catch(() => {});
    }

    if (icNo) {
      try {
        const res = await diversionService.getSourceIcLineItems(icNo, stage);
        if (res && res.data) {
          const items = res.data.map(i => ({
            ...i,
            diversionQty: diversionType === 'COMPLETE' ? i.availableBalanceQty : '',
            remainingBalanceQty: diversionType === 'COMPLETE' ? 0 : (i.availableBalanceQty || 0)
          }));
          setLineItems(items);
        }
      } catch (err) {
        console.error('Error fetching line items:', err);
      }
    } else {
      setLineItems([]);
    }
  };

  const handleQtyChange = (index, val) => {
    const numericVal = parseFloat(val) || 0;
    const updated = [...lineItems];
    const available = updated[index].availableBalanceQty || 0;

    if (numericVal > available) {
      const itemLabel = stage === 'RAW_MATERIAL'
        ? `Heat ${updated[index].heatNo || ''}`
        : `Lot ${updated[index].lotNo || ''}${updated[index].heatNo ? ` (Heat: ${updated[index].heatNo})` : ''}`;
      setError(`Diversion quantity cannot exceed available balance (${available}) for ${itemLabel}`);
    } else {
      setError('');
    }

    updated[index].diversionQty = val;
    updated[index].remainingBalanceQty = Math.max(0, available - numericVal);
    setLineItems(updated);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed for the Railway Permission document.');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must not exceed 5 MB.');
      e.target.value = '';
      return;
    }

    setRailwayDocUploading(true);
    setRailwayDocFileName(file.name);
    setRailwayPermissionDocUrl('');
    try {
      const res = await diversionService.uploadRailwayDoc(file);
      const url = res?.data?.url || res?.url;
      if (!url) throw new Error('No URL returned from upload');
      setRailwayPermissionDocUrl(url);
    } catch (err) {
      setError('Failed to upload Railway Permission document. Please try again.');
      setRailwayDocFileName('');
      e.target.value = '';
    } finally {
      setRailwayDocUploading(false);
    }
  };

  const calculateTotalDiversion = () => {
    return lineItems.reduce((acc, curr) => acc + (parseFloat(curr.diversionQty) || 0), 0);
  };

  const handleSubmit = async () => {
    setError('');

    if (!stage) {
      setError('Please select an Inspection Stage.');
      return;
    }
    if (!plantName) {
      setError('Please select a Manufacturing Plant.');
      return;
    }
    if (!cmUserId) {
      setError('Please select a Controlling Manager.');
      return;
    }
    if (!selectedIcNo) {
      setError('Please select a Source IC Number.');
      return;
    }
    if (!targetPoNo) {
      setError('Please select a Target PO Number.');
      return;
    }
    if (!targetPoSrNo) {
      setError('Please select a Target PO Item Serial Number.');
      return;
    }

    const totalQty = calculateTotalDiversion();
    if (totalQty <= 0) {
      setError('Total diversion quantity must be greater than zero.');
      return;
    }
    if (!reasonCode) {
      setError('Please select a Reason for Diversion.');
      return;
    }
    if (!railwayPermissionNo || !railwayPermissionDocUrl) {
      setError('Railway permission number and document upload are mandatory.');
      return;
    }

    const payload = {
      vendorCode: vendorCode,
      companyName: companyName || vendorUser?.companyName || 'Vendor Company',
      plantId: plantId,
      plantName: plantName || 'Main Plant',
      plantAddress: plantAddress,
      poiCode: poiCode,
      rioId: rioId,
      cmUserId: cmUserId,
      stage: stage,
      diversionType: diversionType,
      sourcePoNo: selectedIcDetails?.poNo || '',
      sourcePoSrNo: selectedIcDetails?.poSerialNo || '',
      sourceIcNo: selectedIcNo,
      sourceCallNo: selectedIcDetails?.callNo || '',
      sourceIcDate: selectedIcDetails?.icDate,
      sourceIeName: selectedIcDetails?.ieName || null,
      sourceIeUserId: selectedIcDetails?.ieUserId || null,
      targetPoNo: targetPoNo,
      targetPoSrNo: targetPoSrNo,
      totalDiversionQty: totalQty,
      unitOfMeasurement: stage === 'RAW_MATERIAL' ? 'MT' : 'NOS',
      reasonCode: reasonCode,
      reasonRemarks: vendorRemarks,
      railwayPermissionNo: railwayPermissionNo,
      railwayPermissionDate: railwayPermissionDate,
      railwayPermissionDocUrl: railwayPermissionDocUrl,
      vendorRemarks: vendorRemarks,
      items: lineItems
        .filter(item => (parseFloat(item.diversionQty) || 0) > 0)
        .map(item => ({
        heatNo: item.heatNo,
        tcNo: item.tcNo,
        lotNo: item.lotNo,
        acceptedQty: item.acceptedQty,
        downstreamConsumedQty: item.downstreamConsumedQty || 0,
        availableBalanceQty: item.availableBalanceQty,
        diversionQty: parseFloat(item.diversionQty) || 0,
        remainingBalanceQty: (item.availableBalanceQty || 0) - (parseFloat(item.diversionQty) || 0)
      }))
    };

    setIsLoading(true);
    try {
      if (isEditMode && initialData?.id) {
        await diversionService.updateRequest(initialData.id, payload);
      } else {
        await diversionService.submitRequest(payload);
      }
      setIsLoading(false);
      onSuccess();
      onClose();
    } catch (err) {
      setIsLoading(false);
      setError(err?.response?.data?.message || err.message || 'Error processing request');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="diversion-modal-overlay">
      <div className="diversion-modal-content">
        <div className="diversion-modal-header">
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
              {isEditMode ? 'Modify Diversion Request' : 'Raise Material Diversion Request'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Transfer inspected & accepted material to a target PO without repeating testing
            </p>
          </div>
          <button className="diversion-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Section 1: Stage & Authority */}
        <div className="diversion-card">
          <div className="diversion-card-title">1. Stage & Controlling Authority</div>
          <div className="diversion-grid">
            <div className="diversion-form-group">
              <label className="diversion-form-label">Inspection Stage</label>
              <ModernSelect
                options={[
                  { value: 'RAW_MATERIAL', label: 'Raw Material (Spring Steel)', icon: '🔩', subtext: 'Raw material heat TCs' },
                  { value: 'PROCESS', label: 'Process Inspection', icon: '⚙️', subtext: 'In-process ERC lot batches' },
                  { value: 'FINAL', label: 'Final Inspection', icon: '🏷️', subtext: 'Finished ERC clips' }
                ]}
                value={stage}
                placeholder="-- Select Inspection Stage --"
                onChange={val => {
                  setStage(val);
                  setSelectedIcNo('');
                  setLineItems([]);
                }}
              />
            </div>
            <div className="diversion-form-group">
              <label className="diversion-form-label">Manufacturing Plant</label>
              <SearchablePlantDropdown
                options={plants}
                value={plantName}
                address={plantAddress}
                poiCode={poiCode}
                onChange={handlePlantSelect}
                placeholder="-- Select Manufacturing Plant --"
              />
            </div>
            <div className="diversion-form-group">
              <label className="diversion-form-label">Regional Office (RIO)</label>
              <div className="diversion-readonly-field">
                <span style={{ fontSize: '15px' }}>🏛️</span>
                <span className="diversion-readonly-tag">{rioId || '—'}</span>
                <span style={{ fontSize: '12.5px', color: '#64748b' }}>{rioId ? 'Inspection Region' : 'Select a plant first'}</span>
              </div>
            </div>
            <div className="diversion-form-group">
              <label className="diversion-form-label">Controlling Manager (ERC)</label>
              <ModernSelect
                options={filteredCmList.map(cm => ({
                  value: String(cm.userId),
                  label: cm.fullName || cm.username || `CM #${cm.userId}`,
                  icon: '👤',
                  subtext: cm.employeeCode ? `${cm.employeeCode} · ${cm.rio || ''}` : (cm.rio || '')
                }))}
                value={cmUserId}
                placeholder={rioId ? '-- Select Controlling Manager --' : '-- Select a plant first --'}
                onChange={setCmUserId}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Source IC & Target PO */}
        <div className="diversion-card">
          <div className="diversion-card-title">2. Source IC & Target PO Selection</div>
          <div className="diversion-grid">
            <div className="diversion-form-group diversion-grid-span-2">
              <label className="diversion-form-label">Source IC Number *</label>
              <SearchableIcDropdown
                options={eligibleIcs}
                value={selectedIcNo}
                onChange={handleIcChange}
                placeholder="-- Search & Select Completed Source IC --"
                loading={loadingIcs}
              />
            </div>
            <div className="diversion-form-group diversion-grid-span-2">
              <label className="diversion-form-label">Source PO & Item Sr.</label>
              <div className="diversion-readonly-field">
                <span style={{ fontSize: '14px' }}>📄</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>
                  {selectedIcDetails ? formatPoAndItem(selectedIcDetails.poNo, selectedIcDetails.poSerialNo) : 'Auto-filled on IC selection'}
                </span>
              </div>
            </div>
            <div className="diversion-form-group diversion-grid-span-2">
              <label className="diversion-form-label">Target PO Number *</label>
              <SearchablePoDropdown
                options={vendorPos}
                value={targetPoNo}
                onChange={handleTargetPoChange}
                placeholder="-- Search & Select Target PO --"
              />
            </div>
            <div className="diversion-form-group diversion-grid-span-2">
              <label className="diversion-form-label">Target PO Item Sr. *</label>
              <SearchablePoItemDropdown
                options={availableTargetPoItems}
                value={targetPoSrNo}
                onChange={setTargetPoSrNo}
                placeholder="-- Select Target Item Sr --"
                disabled={!targetPoNo}
              />
            </div>
          </div>
        </div>

        {/* Section 3: Material Line Grid */}
        {lineItems.length > 0 && (
          <div className="diversion-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div className="diversion-card-title" style={{ margin: 0 }}>3. Material Line Quantities</div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>

                <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="radio" name="diversionTypeOption" checked={diversionType === 'PARTIAL'} onChange={() => {
                    setDiversionType('PARTIAL');
                    const updated = lineItems.map(i => ({ ...i, diversionQty: 0, remainingBalanceQty: i.availableBalanceQty }));
                    setLineItems(updated);
                  }} /> Partial Diversion
                </label>


                <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="radio" name="diversionTypeOption" checked={diversionType === 'COMPLETE'} onChange={() => {
                    setDiversionType('COMPLETE');
                    const updated = lineItems.map(i => ({ ...i, diversionQty: i.availableBalanceQty, remainingBalanceQty: 0 }));
                    setLineItems(updated);
                  }} /> Complete Diversion
                </label>

              </div>
            </div>

            {(selectedIcDetails?.ieName || selectedIcDetails?.certDocUrl || selectedIcNo) && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px', padding: '10px 16px', background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', borderRadius: '10px', border: '1px solid #bfdbfe', boxShadow: '0 1px 3px rgba(59,130,246,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  {selectedIcDetails?.ieName ? (
                    <div>
                      <div style={{ fontSize: '10.5px', color: '#64748b', fontWeight: 500, letterSpacing: '0.3px', textTransform: 'uppercase' }}>Certificate Generated By</div>
                      <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 700, marginTop: '1px' }}>{selectedIcDetails.ieName}</div>
                    </div>
                  ) : (
                    <span style={{ fontSize: '12.5px', color: '#475569', fontWeight: 500 }}>Source IC Selected</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    const token = selectedIcDetails?.certificateNo || selectedIcNo || selectedIcDetails?.callNo;
                    if (!token) {
                      alert('Please select a valid Source IC first.');
                      return;
                    }
                    try {
                      const res = await httpClient.get(`/api/certificate-storage/view?icNumber=${encodeURIComponent(token)}`);
                      const certData = res?.data || res;
                      if (certData && certData.signedData) {
                        const cleanBase64 = String(certData.signedData).replace(/^data:application\/pdf;base64,/, '').trim();
                        const byteCharacters = atob(cleanBase64);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const file = new Blob([byteArray], { type: 'application/pdf' });
                        const fileURL = URL.createObjectURL(file);
                        window.open(fileURL, '_blank');
                        return;
                      }

                      // If no signedData, check url fallback
                      const targetUrl = certData?.url || selectedIcDetails?.certDocUrl;
                      if (targetUrl) {
                        window.open(targetUrl, '_blank');
                      } else {
                        alert('Certificate document is not available in storage.');
                      }
                    } catch (e) {
                      if (selectedIcDetails?.certDocUrl) {
                        window.open(selectedIcDetails.certDocUrl, '_blank');
                      } else {
                        alert('Could not retrieve certificate document from storage.');
                      }
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#fff', background: 'linear-gradient(135deg, #0284c7, #0ea5e9)', border: 'none', padding: '7px 14px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 1px 4px rgba(2,132,199,0.3)', flexShrink: 0 }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  View IC Certificate
                </button>
              </div>
            )}

            <div className="diversion-table-wrapper" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <table className="diversion-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ width: diversionType === 'PARTIAL' ? '14%' : '18%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                      {stage === 'RAW_MATERIAL' ? 'Heat No.' : 'Lot No.'}
                    </th>
                    <th style={{ width: diversionType === 'PARTIAL' ? '15%' : '20%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                      {stage === 'RAW_MATERIAL' ? 'TC No.' : 'Heat No.'}
                    </th>
                    <th style={{ width: diversionType === 'PARTIAL' ? (stage === 'PROCESS' ? '18%' : '22%') : '25%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                      {stage === 'PROCESS' ? 'Qty Accepted in Process' : `Total Accepted (${stage === 'RAW_MATERIAL' ? 'MT' : 'Nos'})`}
                    </th>
                    {stage === 'PROCESS' && diversionType === 'PARTIAL' && (
                      <th style={{ width: '14%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                        Called Offered Earlier
                      </th>
                    )}
                    <th style={{ width: diversionType === 'PARTIAL' ? '13%' : '18%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                      Available Balance
                    </th>
                    <th style={{ width: diversionType === 'PARTIAL' ? '14%' : '19%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                      Diversion Qty Intended *
                    </th>
                    {diversionType === 'PARTIAL' && (
                      <th style={{ width: '12%', padding: '12px 16px', fontSize: '12.5px', fontWeight: '700', color: '#475569' }}>
                        Remaining Source Balance
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#fafbfc' }}>
                      <td style={{ padding: '14px 16px', fontWeight: '600', color: '#1e293b', verticalAlign: 'middle' }}>
                        {stage === 'RAW_MATERIAL' ? (item.heatNo || '-') : (item.lotNo || '-')}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#475569', fontWeight: '500', verticalAlign: 'middle' }}>
                        {stage === 'RAW_MATERIAL' ? (item.tcNo || '-') : (item.heatNo || '-')}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: '600', color: '#334155', verticalAlign: 'middle' }}>
                        {item.acceptedQty}
                      </td>
                      {stage === 'PROCESS' && diversionType === 'PARTIAL' && (
                        <td style={{ padding: '14px 16px', color: '#64748b', fontWeight: '500', verticalAlign: 'middle' }}>
                          {item.downstreamConsumedQty !== undefined ? item.downstreamConsumedQty : 0}
                        </td>
                      )}
                      <td style={{ padding: '14px 16px', color: '#0284c7', fontWeight: '700', fontSize: '13.5px', verticalAlign: 'middle' }}>
                        {item.availableBalanceQty}
                      </td>
                      <td style={{ padding: '10px 16px', verticalAlign: 'middle' }}>
                        <input
                          type="number"
                          step={stage === 'RAW_MATERIAL' ? '0.001' : '1'}
                          className="diversion-input"
                          style={{
                            width: '100%',
                            maxWidth: '135px',
                            padding: '7px 12px',
                            fontSize: '13px',
                            fontWeight: '600',
                            borderRadius: '7px',
                            background: diversionType === 'COMPLETE' ? '#f8fafc' : '#ffffff',
                            border: '1px solid #cbd5e1'
                          }}
                          value={item.diversionQty}
                          disabled={diversionType === 'COMPLETE'}
                          onChange={e => handleQtyChange(idx, e.target.value)}
                        />
                      </td>
                      {diversionType === 'PARTIAL' && (
                        <td style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', verticalAlign: 'middle' }}>
                          {item.remainingBalanceQty !== undefined ? item.remainingBalanceQty : item.availableBalanceQty}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '10px',
              marginTop: '14px',
              padding: '10px 16px',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <span style={{ fontSize: '13.5px', color: '#64748b', fontWeight: '600' }}>Total Diversion Quantity:</span>
              <span style={{ fontSize: '15px', fontWeight: '700', color: '#0284c7' }}>
                {stage === 'RAW_MATERIAL' ? calculateTotalDiversion().toFixed(3) : calculateTotalDiversion()} {stage === 'RAW_MATERIAL' ? 'MT' : 'Nos'}
              </span>
            </div>
          </div>
        )}

        {/* Section 4: Railway Permission & Reason */}
        <div className="diversion-card">
          <div className="diversion-card-title">4. Railway Permission & Reason</div>
          <div className="diversion-grid">
            <div className="diversion-form-group">
              <label className="diversion-form-label">Reason for Diversion</label>
              <ModernSelect
                options={[
                  { value: 'PO_CANCELLATION', label: 'PO Cancellation', icon: '🔄' },
                  { value: 'PO_QUANTITY_REVISION', label: 'PO Quantity Revision', icon: '🔄' }
                ]}
                value={reasonCode}
                placeholder="-- Select Reason for Diversion --"
                onChange={setReasonCode}
              />
            </div>
            <div className="diversion-form-group">
              <label className="diversion-form-label">Railway Approval Letter No. *</label>
              <input className="diversion-input" placeholder="Letter Ref No." value={railwayPermissionNo} onChange={e => setRailwayPermissionNo(e.target.value)} />
            </div>
            <div className="diversion-form-group">
              <label className="diversion-form-label">Approval Date *</label>
              <input type="date" className="diversion-input" value={railwayPermissionDate} onChange={e => setRailwayPermissionDate(e.target.value)} />
            </div>
            <div className="diversion-form-group">
              <label className="diversion-form-label">Upload Railway Permission Doc (PDF) * <span style={{ fontWeight: 400, color: '#64748b', fontSize: '12px' }}>PDF only, max 5 MB</span></label>
              <input type="file" accept="application/pdf,.pdf" className="diversion-input" onChange={handleFileUpload} disabled={railwayDocUploading} />
              {railwayDocUploading && (
                <span style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px', display: 'block' }}>
                  ⏳ Uploading {railwayDocFileName}…
                </span>
              )}
              {!railwayDocUploading && railwayPermissionDocUrl && (
                <span style={{ fontSize: '12px', color: '#16a34a', marginTop: '4px', display: 'block' }}>
                  ✓ Uploaded: {railwayDocFileName}
                </span>
              )}
            </div>
          </div>
          <div className="diversion-form-group" style={{ marginTop: '16px' }}>
            <label className="diversion-form-label">Remarks / Justification</label>
            <textarea className="diversion-textarea" rows="2" placeholder="Enter detailed justification..." value={vendorRemarks} onChange={e => setVendorRemarks(e.target.value)} />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
          <button type="button" className="diversion-tab-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="diversion-btn-primary" style={{ background: '#0284c7', color: '#ffffff' }} onClick={() => handleSubmit(false)} disabled={isLoading || railwayDocUploading}>
            {railwayDocUploading ? 'Uploading document…' : isLoading ? 'Submitting...' : isEditMode ? 'Update Request' : 'Submit to Controlling Manager'}
          </button>
        </div>
      </div>

      {/* Toast notification — fixed bottom-right, always visible */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '28px',
          right: '28px',
          zIndex: 99999,
          background: '#1e293b',
          color: '#fff',
          padding: '14px 20px',
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          maxWidth: '380px',
          animation: 'slideInToast 0.25s ease',
        }}>
          <span style={{ fontSize: '16px' }}>⚠️</span>
          <span>{toast}</span>
          <button
            onClick={() => setToast('')}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
          >✕</button>
        </div>
      )}

      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default RaiseDiversionRequestModal;
